import { useContext } from 'react';
import { parseNumberText } from '@/utils/number';
import { AmountInput } from './AmountInput';
import { QueueDecisionButtons } from './QueueDecisionButtons';
import { DerDraftsContext } from './queueDraftsContext';
import { VerifyStatusBadge } from './VerifyStatusBadge';
import type { OfflineDerRow, BillingLimits } from '../types/billing.types';
import { amountProblem, derVerifiability, getAmountCap } from '../utils/verifiability';

export interface DerQueueColumnDeps {
  mode: 'verify' | 'approve';
  limits: BillingLimits | undefined;
  onDecide: (row: OfflineDerRow, accept: boolean) => void;
  onDownload: (row: OfflineDerRow) => void;
  pendingDetailId: number | null;
  downloadingDetailId: number | null;
  searchText: string | undefined;
}

interface DerQueueCellProps {
  row: OfflineDerRow;
  deps: DerQueueColumnDeps;
}

export function DerCostCell({ row, deps }: DerQueueCellProps) {
  const { drafts, onDraftChange } = useContext(DerDraftsContext);
  const { limits } = deps;
  if (!limits) return null;
  const amountCap = getAmountCap(deps.mode, row.amount, row.approveAmount);
  const value = drafts[row.detailId] ?? String(amountCap.amount);
  const settled =
    deps.mode === 'verify' ? row.status !== 'pending' : row.approveStatus !== 'pending';

  return (
    <AmountInput
      id={`der-amount-${row.detailId}`}
      label={`Amount to approve for ${row.name}`}
      value={value}
      cap={amountCap}
      disabled={settled}
      onChange={(next) => onDraftChange(row.detailId, next)}
      problem={
        settled
          ? undefined
          : amountProblem(parseNumberText(value), amountCap, limits.derFare, 'amount')
      }
    />
  );
}

export function DerDecisionCell({ row, deps }: DerQueueCellProps) {
  const { drafts } = useContext(DerDraftsContext);
  const { limits } = deps;
  if (!limits) return null;
  const { canDecide, reason } = derVerifiability(row, limits, deps.mode);
  const amountCap = getAmountCap(deps.mode, row.amount, row.approveAmount);
  const value = drafts[row.detailId] ?? String(amountCap.amount);
  const problem = amountProblem(parseNumberText(value), amountCap, limits.derFare, 'amount');
  const busy = deps.pendingDetailId === row.detailId;
  const settled =
    deps.mode === 'verify' ? row.status !== 'pending' : row.approveStatus !== 'pending';

  if (settled) {
    return (
      <VerifyStatusBadge
        status={deps.mode === 'verify' ? row.status : row.approveStatus}
        decisionKind={deps.mode}
      />
    );
  }

  return (
    <QueueDecisionButtons
      acceptLabel={deps.mode === 'verify' ? 'Verify' : 'Approve'}
      isAcceptDisabled={!canDecide || problem !== undefined || busy}
      isBusy={busy}
      acceptHint={reason ?? problem}
      reason={reason}
      onAccept={() => deps.onDecide(row, true)}
      onReject={() => deps.onDecide(row, false)}
    />
  );
}
